/**
 * Languages Management Page
 * Multi-Language System
 *
 * Admin page for managing languages and translations.
 * Features:
 * - Language CRUD operations
 * - Translation table with inline editing
 * - Import/export functionality
 * - Namespace filtering
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '../components/layout';
import { Button, Card } from '../components/ui';

import { ResponsiveModal } from '../components/responsive/ResponsiveModal';
import { useAuth } from '../contexts';
import { languagesService, Language, PredefinedLanguage } from '../services/languagesService';
import { logger } from '../services/loggerService';

// Styles
const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  button: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  primaryButton: {
    backgroundColor: 'var(--color-primary)',
    color: 'white',
  },
  secondaryButton: {
    backgroundColor: 'var(--color-background-card)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border-default)',
  },
  dangerButton: {
    backgroundColor: '#dc2626',
    color: 'white',
  },
  section: {
    backgroundColor: 'var(--color-background-card, #ffffff)',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '24px',
    border: '1px solid var(--color-border-default, #e5e7eb)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    margin: '0 0 16px 0',
  },
  languageGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  languageCard: {
    padding: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-border-default)',
    minWidth: '140px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  languageCardActive: {
    borderColor: 'var(--color-primary)',
    backgroundColor: 'var(--color-primary-light, rgba(59, 130, 246, 0.1))',
  },
  languageCardDefault: {
    borderStyle: 'dashed' as const,
  },
  languageFlag: {
    fontSize: '32px',
    marginBottom: '8px',
  },
  languageCode: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  languageName: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
  },
  addCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
    border: '2px dashed var(--color-border-default)',
    backgroundColor: 'transparent',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap' as const,
  },
  select: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid var(--color-border-default)',
    backgroundColor: 'var(--color-background-card)',
    color: 'var(--color-text-primary)',
    fontSize: '14px',
  },
  searchInput: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid var(--color-border-default)',
    backgroundColor: 'var(--color-background-card)',
    color: 'var(--color-text-primary)',
    fontSize: '14px',
    flex: 1,
    minWidth: '200px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '14px',
  },
  th: {
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid var(--color-border-default)',
    color: 'var(--color-text-secondary)',
    fontWeight: 600,
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid var(--color-border-default)',
    color: 'var(--color-text-primary)',
  },
  editableCell: {
    cursor: 'text',
    padding: '8px',
    borderRadius: '4px',
  },
  cellInput: {
    width: '100%',
    padding: '8px',
    border: '1px solid var(--color-primary)',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'var(--color-background-card)',
    color: 'var(--color-text-primary)',
  },
  emptyCell: {
    color: 'var(--color-text-muted)',
    fontStyle: 'italic' as const,
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '11px',
    fontWeight: 500,
  },
  defaultBadge: {
    backgroundColor: 'var(--color-primary)',
    color: 'white',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid var(--color-border-default)',
    backgroundColor: 'var(--color-background-card)',
    color: 'var(--color-text-primary)',
    fontSize: '14px',
  },
  toast: {
    position: 'fixed' as const,
    bottom: '24px',
    right: '24px',
    padding: '12px 24px',
    borderRadius: '8px',
    backgroundColor: '#10b981',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    zIndex: 1001,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  toastError: {
    backgroundColor: '#ef4444',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
  },
};

/**
 * Translation row interface
 */
interface TranslationRow {
  key: string;
  values: Record<string, string>;
}

/**
 * Toast notification state
 */
interface Toast {
  message: string;
  type: 'success' | 'error';
}

/**
 * Languages Page Component
 */
const LanguagesPage: React.FC = () => {
  const { t } = useTranslation('languages');
  const { hasPermission } = useAuth();

  // State
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [selectedNamespace, setSelectedNamespace] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<Toast | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Available predefined languages for simplified add
  const [availableLanguages, setAvailableLanguages] = useState<PredefinedLanguage[]>([]);
  const [selectedNewLanguage, setSelectedNewLanguage] = useState<string | null>(null);
  const [addLanguageSearch, setAddLanguageSearch] = useState('');

  // Form states
  const [importData, setImportData] = useState({ json: '', namespace: '', merge: true });

  // Editing cell state
  const [editingCell, setEditingCell] = useState<{ key: string; lang: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  // CA-20 FIX: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  /**
   * Show toast notification
   */
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  /**
   * Load languages
   */
  const loadLanguages = useCallback(async () => {
    try {
      const data = await languagesService.getAll(true);
      setLanguages(data);
      if (data.length > 0 && !selectedLanguage) {
        setSelectedLanguage(data[0].code);
      }
    } catch (error) {
      logger.error('Failed to load languages', error);
      showToast(t('toast.loadError'), 'error');
    }
  }, [selectedLanguage]);

  /**
   * Load namespaces
   */
  const loadNamespaces = useCallback(async () => {
    try {
      const data = await languagesService.getNamespaces();
      setNamespaces(data);
    } catch (error) {
      logger.error('Failed to load namespaces', error);
    }
  }, []);

  /**
   * Load translations for all languages
   */
  const loadTranslations = useCallback(async () => {
    if (languages.length === 0) return;

    try {
      const translationData: Record<string, Record<string, string>> = {};

      for (const lang of languages) {
        try {
          const flat = await languagesService.getFlatTranslations(lang.code);
          translationData[lang.code] = flat;
        } catch {
          translationData[lang.code] = {};
        }
      }

      setTranslations(translationData);
    } catch (error) {
      logger.error('Failed to load translations', error);
    }
  }, [languages]);

  /**
   * Initialize
   */
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await loadLanguages();
      await loadNamespaces();
      await loadAvailableLanguages();
      setIsLoading(false);
    };
    init();
  }, []);

  /**
   * Load translations when languages change
   */
  useEffect(() => {
    if (languages.length > 0) {
      loadTranslations();
    }
  }, [languages, loadTranslations]);

  /**
   * Get all unique translation keys
   */
  const translationRows = useMemo<TranslationRow[]>(() => {
    const allKeys = new Set<string>();

    Object.values(translations).forEach(langTranslations => {
      Object.keys(langTranslations).forEach(key => allKeys.add(key));
    });

    const rows: TranslationRow[] = Array.from(allKeys)
      .filter(key => {
        // Filter by namespace
        if (selectedNamespace !== 'all') {
          if (!key.startsWith(selectedNamespace + '.')) {
            return false;
          }
        }
        // Filter by search query
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          if (!key.toLowerCase().includes(query)) {
            // Check if any translation contains the query
            const hasMatch = Object.values(translations).some(
              langTrans => langTrans[key]?.toLowerCase().includes(query)
            );
            if (!hasMatch) return false;
          }
        }
        return true;
      })
      .map(key => ({
        key,
        values: Object.fromEntries(
          languages.map(lang => [lang.code, translations[lang.code]?.[key] || ''])
        ),
      }))
      .sort((a, b) => a.key.localeCompare(b.key));

    return rows;
  }, [translations, languages, selectedNamespace, searchQuery]);

  // CA-20 FIX: Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedNamespace, searchQuery]);

  // CA-20 FIX: Calculate pagination values
  const totalPages = Math.ceil(translationRows.length / itemsPerPage);
  const paginatedRows = translationRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /**
   * Load available predefined languages
   */
  const loadAvailableLanguages = useCallback(async () => {
    try {
      const data = await languagesService.getAvailable();
      setAvailableLanguages(data);
    } catch (error) {
      logger.error('Failed to load available languages', error);
    }
  }, []);

  /**
   * Handle create language (simplified - from predefined list)
   */
  const handleCreateLanguage = async () => {
    if (!selectedNewLanguage) return;

    try {
      await languagesService.createFromCode(selectedNewLanguage);
      showToast(t('toast.created'));
      setShowCreateModal(false);
      setSelectedNewLanguage(null);
      setAddLanguageSearch('');
      await loadLanguages();
      await loadAvailableLanguages();
    } catch (error: any) {
      showToast(error.response?.data?.message || t('toast.createError'), 'error');
    }
  };

  /**
   * Handle delete language
   */
  const handleDeleteLanguage = async (code: string) => {
    if (!confirm(t('deleteConfirm'))) {
      return;
    }

    try {
      await languagesService.delete(code);
      showToast(t('toast.deleted'));
      await loadLanguages();
      await loadAvailableLanguages();
    } catch (error: any) {
      showToast(error.response?.data?.message || t('toast.deleteError'), 'error');
    }
  };

  /**
   * Handle set language as default
   * CA-18: Allow changing the default language
   */
  const handleSetDefault = async (code: string) => {
    if (!confirm(t('setDefaultConfirm'))) {
      return;
    }

    try {
      await languagesService.setDefault(code);
      showToast(t('toast.setDefault'));
      await loadLanguages();
    } catch (error: any) {
      showToast(error.response?.data?.message || t('toast.setDefaultError'), 'error');
    }
  };

  /**
   * Handle cell edit save
   */
  const handleCellSave = async () => {
    if (!editingCell) return;

    const { key, lang } = editingCell;
    const [namespace, ...keyParts] = key.split('.');
    const translationKey = keyParts.join('.');

    // Build nested object from flat key
    const buildNestedObject = (keys: string[], value: string): Record<string, unknown> => {
      if (keys.length === 1) {
        return { [keys[0]]: value };
      }
      return { [keys[0]]: buildNestedObject(keys.slice(1), value) };
    };

    try {
      await languagesService.updateTranslations(lang, {
        namespace,
        translations: buildNestedObject(keyParts, editValue),
      });

      // Update local state
      setTranslations(prev => ({
        ...prev,
        [lang]: {
          ...prev[lang],
          [key]: editValue,
        },
      }));

      setEditingCell(null);
      setEditValue('');
    } catch (error: any) {
      showToast(error.response?.data?.message || t('toast.saveError'), 'error');
    }
  };

  /**
   * Handle import
   */
  const handleImport = async () => {
    if (!selectedLanguage) return;

    try {
      const content = JSON.parse(importData.json);
      await languagesService.importTranslations(selectedLanguage, {
        namespace: importData.namespace || undefined,
        content,
        merge: importData.merge,
      });
      showToast(t('toast.imported'));
      setShowImportModal(false);
      setImportData({ json: '', namespace: '', merge: true });
      await loadTranslations();
    } catch (error: any) {
      showToast(error.response?.data?.message || t('toast.importError'), 'error');
    }
  };

  /**
   * Handle export
   */
  const handleExport = async () => {
    if (!selectedLanguage) return;

    try {
      const data = await languagesService.exportTranslations(
        selectedLanguage,
        selectedNamespace !== 'all' ? selectedNamespace : undefined
      );

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedLanguage}${selectedNamespace !== 'all' ? `-${selectedNamespace}` : ''}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      showToast(error.response?.data?.message || t('toast.exportError'), 'error');
    }
  };

  if (isLoading) {
    return (
      <Container className="py-8">
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-text-secondary)' }}>
          {t('loading')}
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-8">
      {/* Header */}
      <div className="page-header page-header--with-actions">
        <div>
          <h1 className="page-title">{t('title')}</h1>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowImportModal(true)}
          >
            {t('importJson')}
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
          >
            {t('exportJson')}
          </Button>
        </div>
      </div>

      {/* Languages Section */}
      <div style={styles.section}>
        <h2 className="card-title mb-4">{t('availableLanguages')}</h2>
        <div style={styles.languageGrid}>
          {languages.map(lang => (
            <div
              key={lang.code}
              style={{
                ...styles.languageCard,
                ...(selectedLanguage === lang.code ? styles.languageCardActive : {}),
                display: 'flex',
                flexDirection: 'column' as const,
                alignItems: 'center',
                minHeight: hasPermission('languages.manage') ? '200px' : 'auto',
              }}
              onClick={() => setSelectedLanguage(lang.code)}
            >
              <div style={styles.languageFlag}>{lang.emoji_flag}</div>
              <div style={styles.languageCode}>{lang.code.toUpperCase()}</div>
              <div style={styles.languageName}>{lang.native_name}</div>

              {/* Default badge / Set Default row - fixed height for alignment */}
              {hasPermission('languages.manage') && (
                <div style={{ marginTop: '8px', minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {lang.is_default ? (
                    <span style={{ ...styles.badge, ...styles.defaultBadge }}>
                      {t('default')}
                    </span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleSetDefault(lang.code); }}
                    >
                      {t('setDefault')}
                    </Button>
                  )}
                </div>
              )}
              {!hasPermission('languages.manage') && lang.is_default && (
                <span style={{ ...styles.badge, ...styles.defaultBadge, marginTop: '8px' }}>
                  {t('default')}
                </span>
              )}

              {/* Delete button - pushed to bottom */}
              {hasPermission('languages.manage') && !lang.is_default && (
                <div style={{ marginTop: 'auto', paddingTop: '8px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDeleteLanguage(lang.code); }}
                  >
                    {t('delete')}
                  </Button>
                </div>
              )}
            </div>
          ))}
          {hasPermission('languages.manage') && (
            <div
              style={{ ...styles.languageCard, ...styles.addCard }}
              onClick={() => { loadAvailableLanguages(); setShowCreateModal(true); }}
            >
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>+</div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                {t('addLanguage')}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Translations Section */}
      <div style={styles.section}>
        <h2 className="card-title mb-4">{t('translations')}</h2>

        {/* Filter Bar */}
        {/* CA-19 FIX: Translate namespace dropdown items */}
        <div style={styles.filterBar}>
          <select
            style={styles.select}
            value={selectedNamespace}
            onChange={(e) => setSelectedNamespace(e.target.value)}
          >
            <option value="all">{t('allNamespaces')}</option>
            {namespaces.map(ns => (
              <option key={ns} value={ns}>{t(`namespaces.${ns}`, ns)}</option>
            ))}
          </select>
          <input
            type="text"
            style={styles.searchInput}
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Translations Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, minWidth: '200px' }}>{t('key')}</th>
                {languages.map(lang => (
                  <th key={lang.code} style={{ ...styles.th, minWidth: '200px' }}>
                    {lang.emoji_flag} {lang.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* CA-20 FIX: Use paginatedRows instead of fixed slice */}
              {paginatedRows.map(row => (
                <tr key={row.key}>
                  <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '12px' }}>
                    {row.key}
                  </td>
                  {languages.map(lang => {
                    const isEditing = editingCell?.key === row.key && editingCell?.lang === lang.code;
                    const value = row.values[lang.code];

                    return (
                      <td
                        key={lang.code}
                        style={{
                          ...styles.td,
                          ...styles.editableCell,
                          ...(isEditing ? { padding: '4px' } : {}),
                          ...(!value ? styles.emptyCell : {}),
                        }}
                        onClick={() => {
                          if (!hasPermission('languages.manage')) return;
                          setEditingCell({ key: row.key, lang: lang.code });
                          setEditValue(value);
                        }}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            style={styles.cellInput}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleCellSave}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCellSave();
                              if (e.key === 'Escape') {
                                setEditingCell(null);
                                setEditValue('');
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          value || t('empty')
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CA-20 FIX: Full pagination controls */}
        {translationRows.length > 0 && (
          <div style={styles.pagination}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
              {t('showingCount', {
                shown: Math.min(paginatedRows.length, itemsPerPage),
                total: translationRows.length,
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, translationRows.length)
              })}
            </span>

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Items per page selector */}
                <select
                  style={{ ...styles.select, minWidth: 'auto' }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
                <span style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                  {t('perPage', 'pro Seite')}
                </span>

                {/* Page navigation */}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  aria-label={t('pagination.first', 'Erste Seite')}
                >
                  «
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  aria-label={t('pagination.previous', 'Vorherige Seite')}
                >
                  ‹
                </Button>

                <span style={{
                  padding: '4px 12px',
                  color: 'var(--color-text-primary)',
                  fontSize: '14px'
                }}>
                  {t('pagination.pageOf', { current: currentPage, total: totalPages })}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  aria-label={t('pagination.next', 'Nächste Seite')}
                >
                  ›
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  aria-label={t('pagination.last', 'Letzte Seite')}
                >
                  »
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Language Modal - Simplified language picker */}
      <ResponsiveModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setSelectedNewLanguage(null); setAddLanguageSearch(''); }}
        title={t('modal.create.title')}
        size="md"
        data-testid="create-language-modal"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <Button
              variant="outline"
              onClick={() => { setShowCreateModal(false); setSelectedNewLanguage(null); setAddLanguageSearch(''); }}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateLanguage}
              disabled={!selectedNewLanguage}
            >
              {t('create')}
            </Button>
          </div>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('modal.create.selectLanguageLabel', 'Select a language')}</label>
          <input
            type="text"
            style={styles.input}
            value={addLanguageSearch}
            onChange={(e) => setAddLanguageSearch(e.target.value)}
            placeholder={t('modal.create.searchPlaceholder', 'Search languages...')}
          />
        </div>
        <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid var(--color-border-default)', borderRadius: '6px' }}>
          {availableLanguages
            .filter(lang => {
              if (!addLanguageSearch) return true;
              const q = addLanguageSearch.toLowerCase();
              return lang.name.toLowerCase().includes(q)
                || lang.native_name.toLowerCase().includes(q)
                || lang.code.toLowerCase().includes(q);
            })
            .map((lang, index, filtered) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => setSelectedNewLanguage(lang.code)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  borderBottom: index === filtered.length - 1 ? 'none' : '1px solid var(--color-border-default)',
                  backgroundColor: selectedNewLanguage === lang.code ? 'var(--color-primary-light, rgba(59, 130, 246, 0.1))' : 'transparent',
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textAlign: 'left' as const,
                  borderRadius: index === 0 ? '6px 6px 0 0' : index === filtered.length - 1 ? '0 0 6px 6px' : '0',
                }}
                onMouseEnter={(e) => {
                  if (selectedNewLanguage !== lang.code) {
                    e.currentTarget.style.backgroundColor = 'var(--color-background-surface)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedNewLanguage !== lang.code) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '22px' }}>{lang.emoji_flag}</span>
                <span style={{ fontWeight: 500 }}>{lang.name}</span>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>
                  ({lang.native_name})
                </span>
                <span style={{ color: 'var(--color-text-muted)', marginLeft: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
                  {lang.code.toUpperCase()}
                </span>
                {selectedNewLanguage === lang.code && (
                  <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>&#10003;</span>
                )}
              </button>
            ))
          }
          {availableLanguages.filter(lang => {
            if (!addLanguageSearch) return true;
            const q = addLanguageSearch.toLowerCase();
            return lang.name.toLowerCase().includes(q)
              || lang.native_name.toLowerCase().includes(q)
              || lang.code.toLowerCase().includes(q);
          }).length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              {t('modal.create.noLanguagesAvailable', 'No languages available')}
            </div>
          )}
        </div>
      </ResponsiveModal>

      {/* Import Modal */}
      <ResponsiveModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={t('modal.import.title')}
        size="lg"
        data-testid="import-translations-modal"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <Button
              variant="outline"
              onClick={() => setShowImportModal(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
            >
              {t('import')}
            </Button>
          </div>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('modal.import.languageLabel', { code: selectedLanguage?.toUpperCase() })}</label>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('modal.import.namespaceLabel')}</label>
          <select
            style={styles.input}
            value={importData.namespace}
            onChange={(e) => setImportData({ ...importData, namespace: e.target.value })}
          >
            <option value="">{t('modal.import.allNested')}</option>
            {namespaces.map(ns => (
              <option key={ns} value={ns}>{ns}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('modal.import.jsonLabel')}</label>
          <textarea
            style={{ ...styles.input, minHeight: '200px', fontFamily: 'monospace', fontSize: '12px' }}
            value={importData.json}
            onChange={(e) => setImportData({ ...importData, json: e.target.value })}
            placeholder='{"key": "value", ...}'
          />
        </div>
        <div style={styles.formGroup}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={importData.merge}
              onChange={(e) => setImportData({ ...importData, merge: e.target.checked })}
            />
            {t('modal.import.mergeLabel')}
          </label>
        </div>
      </ResponsiveModal>

      {/* Toast */}
      {toast && (
        <div style={{ ...styles.toast, ...(toast.type === 'error' ? styles.toastError : {}) }}>
          {toast.message}
        </div>
      )}
    </Container>
  );
};

export default LanguagesPage;
